import React, { useState, useEffect } from 'react';
import { ExternalLink, Clock, Award, TrendingUp } from 'lucide-react';
import axios from 'axios';

const CaseStudiesSection = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [selectedCase, setSelectedCase] = useState(0);
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8788';

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/content/case-studies`);
        setCaseStudies(response.data);
      } catch (error) {
        console.error('Error fetching case studies:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, [backendUrl]);

  if (loading) {
    return (
      <div className="py-20 bg-gray-900 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-silver border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (caseStudies.length === 0) return null;

  const current = caseStudies[selectedCase];

  return (
    <section id="case-studies" className="py-20 bg-gradient-to-b from-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-silver/60 text-sm font-mono tracking-widest uppercase mb-4">
            Success Stories
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-silver mb-6">
            Transforming Businesses
            <span className="block gradient-text">One Project at a Time</span>
          </h2>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto leading-relaxed">
            See how we've helped enterprise clients overcome complex challenges
            and achieve measurable results through innovative technology solutions.
          </p>
        </div>

        {/* Case Study Selector */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {caseStudies.map((caseStudy, index) => (
            <button
              key={caseStudy.id}
              onClick={() => setSelectedCase(index)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${selectedCase === index
                  ? 'bg-silver text-black'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-silver'
                }`}
            >
              {caseStudy.client || `Case Study ${index + 1}`}
            </button>
          ))}
        </div>

        {/* Selected Case Study */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Case Study Content */}
          <div className="order-2 lg:order-1">
            <div className="mb-6">
              {current.category && (
                <span className="inline-block px-3 py-1 bg-silver/20 text-silver text-sm font-medium rounded-full mb-4">
                  {current.category}
                </span>
              )}
              <h3 className="text-3xl md:text-4xl font-bold text-silver mb-4">
                {current.title}
              </h3>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                {current.description}
              </p>
            </div>

            {/* Results */}
            {current.results && current.results.length > 0 && (
              <div className="mb-8">
                <h4 className="text-xl font-semibold text-silver mb-4 flex items-center gap-2">
                  <TrendingUp size={20} />
                  Key Results
                </h4>
                <div className="grid sm:grid-cols-3 gap-4">
                  {current.results.map((result, index) => (
                    <div key={index} className="glass-morphism rounded-lg p-4 text-center">
                      <div className="text-xl font-bold text-silver mb-1">{result}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <button className="group flex items-center gap-2 bg-silver text-black px-6 py-3 rounded-full font-semibold hover:bg-white transition-all duration-300 hover:scale-105">
              Request Full Demo
              <ExternalLink
                size={16}
                className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300"
              />
            </button>
          </div>

          {/* Visual Element */}
          <div className="order-1 lg:order-2">
            <div
              className="relative rounded-2xl p-12 min-h-[400px] flex items-center justify-center transition-all duration-500 bg-gradient-to-br from-gray-800 to-black border border-white/5 shadow-2xl overflow-hidden"
            >
              {/* Decorative Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="grid grid-cols-8 grid-rows-8 h-full w-full">
                  {Array.from({ length: 64 }).map((_, i) => (
                    <div
                      key={i}
                      className="border border-white/20"
                    ></div>
                  ))}
                </div>
              </div>

              {/* Central Content */}
              <div className="relative z-10 text-center">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-silver to-white flex items-center justify-center shadow-lg">
                  <Award size={40} className="text-black" />
                </div>
                <h5 className="text-2xl font-bold text-silver mb-2">
                  {current.client}
                </h5>
                <p className="text-gray-400 uppercase tracking-widest text-xs">
                  Success Metrics Achieved
                </p>
              </div>

              {/* Floating Elements */}
              <div className="absolute top-4 right-4 w-3 h-3 bg-silver rounded-full animate-pulse"></div>
              <div className="absolute bottom-4 left-4 w-2 h-2 bg-silver/60 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;
